// @ts-nocheck
/**
 * Dual-format time picker: `14:35 / 02:35 PM`. The time is typed in 24h (the
 * only editable part); the 12h AM/PM reading next to it is display-only.
 * Users (especially estimate creators) were picking AM when they meant PM —
 * the mistake wasn't visible until a client complained. Typing 24h removes the
 * meridian choice, and the 12h reading confirms the time at a glance.
 *
 * Both exports (`CustomTimeInput`, `MobileTimePickerWithIcon`) render one
 * 24h `<TimeField>` plus the read-only 12h text inside a single bordered Box.
 * `MobileTimePickerWithIcon` also exposes a clock icon button that opens a
 * hidden `<MobileTimePicker>` dial (24h as well).
 *
 * Key invariants (don't undo these without re-introducing the bugs):
 *   1) The field is controlled with `value={innerValue}` (single source of
 *      truth via useState + useTrackedValue), and the 12h text reads the same
 *      value, so both always agree.
 *   2) `sameTime` guard in onChange prevents ping-pong onChange loops between
 *      the field and the dial.
 *   3) MUI `MuiPickersSectionList` text defaults to black on dark theme →
 *      force `color: white !important` on the section selectors below.
 *   4) Strip `format`, `ampm`, `width` from caller props. Legacy callers pass
 *      `format="hh:mm A" ampm sx={{ width: "115px !important" }}` which
 *      undoes the dual format and clips the layout.
 *   5) Conditional border colors come in via `sx={{ "& fieldset": { borderColor }}}`.
 *      The wrapper has no fieldset — extract that value from sx and apply it
 *      to the outer Box directly. Same for `"& input": { color }`.
 */

import { useEffect, useState } from "react";
import { Box, IconButton, Typography, styled } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimeField } from "@mui/x-date-pickers/TimeField";
import { MobileTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";

const InlineTimeField = styled(TimeField)(() => ({
  // This project's MUI version uses the legacy single-input TimeField
  // (`<FormControl>` → `<OutlinedInput>` → `<input>`) — NOT the newer
  // MultiSection variant with .MuiPickersSectionList-*. So we constrain the
  // <input> itself using `size` doesn't work in MUI v8 either; instead force
  // width:fit-content + a `field-sizing: content` hint where supported.
  width: "fit-content !important",
  minWidth: "0 !important",
  "& .MuiFormControl-root, & .MuiTextField-root": {
    width: "fit-content !important",
    minWidth: "0 !important",
  },
  "& .MuiInputBase-root, & .MuiOutlinedInput-root": {
    padding: 0,
    background: "transparent",
    color: "white",
    width: "fit-content !important",
    minWidth: "0 !important",
  },
  "& input.MuiInputBase-input, & input.MuiOutlinedInput-input": {
    color: "white",
    padding: 0,
    background: "transparent",
    // Native CSS `field-sizing: content` makes the input shrink to its value
    // (supported in Chrome/Edge 123+). Plus an explicit `size` fallback via
    // `width: auto` so older engines render at least at a tight default.
    fieldSizing: "content",
    width: "auto",
    minWidth: 0,
  },
  // MultiSection variant selectors — kept in case some env serves the newer
  // TimeField; harmless if not matched.
  "& .MuiPickersInputBase-root": {
    padding: 0,
    background: "transparent",
    color: "white",
    width: "fit-content",
    minWidth: 0,
  },
  "& .MuiPickersSectionList-root": {
    padding: 0,
    color: "white",
    width: "fit-content !important",
    minWidth: "0 !important",
  },
  "& .MuiPickersSectionList-section": { color: "white !important" },
  "& .MuiPickersSectionList-sectionContent": { color: "white !important" },
  "& .MuiPickersSectionList-sectionSeparator": { color: "white !important" },
  "& span": { color: "white !important", fontSize: "inherit" },
  "& input::selection": { background: "#d175b6" },
  "& fieldset": { border: "none !important" },
}));

const separatorStyles = {
  color: "rgba(255, 255, 255, 0.45)",
  // No internal padding — `space-evenly` on the parent already gives the
  // "/" equal gaps on both sides as its own child.
  padding: 0,
  margin: 0,
  userSelect: "none",
  fontSize: "inherit",
};

const meridianReadingStyles = {
  color: "rgba(255, 255, 255, 0.6)",
  padding: 0,
  margin: 0,
  userSelect: "none",
  fontSize: "inherit",
  whiteSpace: "nowrap",
};

/** 12h reading of the current value ("02:35 PM"), or a placeholder while empty/partial. */
const toMeridianReading = (value) => {
  const time = value && dayjs.isDayjs(value) ? value : value ? dayjs(value) : null;
  return time && time.isValid() ? time.format("hh:mm A") : "--:-- --";
};

const sameTime = (a, b) => {
  const da = a && dayjs.isDayjs(a) ? a : a ? dayjs(a) : null;
  const db = b && dayjs.isDayjs(b) ? b : b ? dayjs(b) : null;
  if (!da && !db) return true;
  if (!da || !db) return false;
  if (!da.isValid() || !db.isValid()) return da.isValid() === db.isValid();
  return da.hour() === db.hour() && da.minute() === db.minute();
};

const useTrackedValue = ({ value, defaultValue }) => {
  const [innerValue, setInnerValue] = useState(
    value !== undefined ? value : defaultValue ?? null,
  );
  useEffect(() => {
    if (value !== undefined) setInnerValue(value);
  }, [value]);
  return [innerValue, setInnerValue];
};

const DualFormatBox = ({
  innerValue,
  handleChange,
  readOnly,
  disabled,
  outerSx,
  fontSize,
  withClockButton,
  onClockClick,
}) => {
  // Callers pass conditional border/text colors via:
  //   sx={{ "& fieldset": { borderColor }, "& input": { color } }}
  // because the legacy component was a styled MUI picker with a real fieldset.
  // Our wrapper has no fieldset → extract those out and apply directly to Box.
  const fieldsetSx = (outerSx && outerSx["& fieldset"]) || {};
  const inputSx = (outerSx && outerSx["& input"]) || {};
  const callerBorderColor =
    fieldsetSx.borderColor || outerSx?.borderColor || "#d175b6";
  const callerTextColor = inputSx.color || outerSx?.color || "white";
  // Strip the legacy `width: "115px !important"` that some callers pass —
  // that value is too narrow for dual format and was the original sizing bug.
  // BUT honor any other width (e.g. `width: "100%"` from modal Grid cells
  // that want the picker to fill the column).
  const { "& fieldset": _f, "& input": _i, width: callerWidth, ...rest } =
    outerSx || {};
  void _f;
  void _i;
  const isLegacyNarrowWidth =
    typeof callerWidth === "string" &&
    /important/i.test(callerWidth) &&
    /\b1\d\dpx\b/.test(callerWidth);
  const passthroughSx = isLegacyNarrowWidth
    ? rest
    : callerWidth !== undefined
      ? { ...rest, width: callerWidth }
      : rest;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        // Children at natural width, no padding/no margin/no gap.
        // Pick the distribution that organizes them best. Options:
        //   - "space-between" : first stuck-left, last stuck-right, others
        //                       distribute the leftover gap evenly between.
        //   - "space-around"  : equal gap AROUND each child (half-gap on the
        //                       outer edges).
        //   - "space-evenly"  : equal gap between every pair AND on outer
        //                       edges (the visually-most-even option).
        //   - "center"        : all 4 clump in the middle, padding around.
        //   - "flex-start"    : all 4 clump to the left (default flex).
        //   - "flex-end"      : all 4 clump to the right.
        // space-evenly = 5 equal gaps with 4 children (before 1st, between
        // each pair, after last). That makes `/` truly equidistant from PM
        // and 14:35 — space-between was leaving leftover unevenly between
        // children of different widths.
        justifyContent: "space-evenly",
        flexWrap: "nowrap",
        gap: 0,
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: callerBorderColor,
        borderRadius: "12px",
        // Vertical padding matches the height of MUI <Select> neighbors
        // (Date Start / Complexity) so the row looks aligned — ~48-50px total
        // box height with the 12px y-padding + ~22px content + 2px border.
        padding: "12px 0",
        color: callerTextColor,
        fontSize: fontSize ?? "12px",
        lineHeight: 1.15,
        background: "transparent",
        boxSizing: "border-box",
        whiteSpace: "nowrap",
        // Default: hug content (table cells where the column is wide get a
        // tight box). Callers that need to fill a bounded parent (modal Grid
        // 25% cells) override via `sx={{ width: "100%" }}` — see EditPosition,
        // AddNewPosition, etc.
        // `minWidth: 180px` gives the table-cell pickers (Actual Start, 1st
        // Break, In, 2nd Break, Actual End) a comfortable width with breathing
        // room for the dual format without the values touching the borders.
        width: "fit-content",
        minWidth: "180px",
        flexShrink: 0,
        ...passthroughSx,
      }}
    >
      {/* Each TimeField is wrapped in a Box that forces hug-content sizing.
          MUI's `styled(TimeField)` ignores `width: fit-content !important` on
          its root (the PickersTextField has internal sizing), so we constrain
          it from the OUTSIDE via a Box wrapper with explicit `width: max-content`.
          That leaves `space-evenly` actual room to distribute gaps. */}
      <Box sx={{ display: "inline-flex", width: "max-content" }}>
        <InlineTimeField
          value={innerValue ?? null}
          onChange={handleChange}
          readOnly={readOnly}
          disabled={disabled}
          format="HH:mm"
          ampm={false}
          spellCheck={false}
        />
      </Box>
      <Typography component="span" sx={separatorStyles}>
        /
      </Typography>
      <Typography component="span" sx={meridianReadingStyles} aria-label="12-hour time">
        {toMeridianReading(innerValue)}
      </Typography>
      {withClockButton && (
        <IconButton
          onClick={onClockClick}
          disabled={disabled || readOnly}
          size="small"
          sx={{
            color: "inherit",
            padding: "1px",
            margin: 0,
            flexShrink: 0,
          }}
        >
          <AccessTimeIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>
  );
};

/**
 * Propagates the external `onChange` ONLY when `newValue` is valid (or null/clear).
 * Without this, callers like AddNewPositionModal do `e.$H` which becomes NaN during
 * partial typing (e.g. the user typed only the minutes), `setHours(NaN)`
 * corrupts the state, the re-render passes a garbage value, and the input seems to
 * stop accepting anything. We always keep `setInnerValue` so the user can see what
 * they are currently typing.
 */
const isValidOrClear = (v) => {
  if (v === null || v === undefined) return true;
  if (dayjs.isDayjs(v)) return v.isValid();
  return false;
};

export const CustomTimeInput = ({
  value,
  defaultValue,
  onChange,
  // Caller-supplied format/ampm are deliberately ignored — wrapper enforces dual.
  format: _ignoredFormat,
  ampm: _ignoredAmpm,
  readOnly,
  disabled,
  sx,
}) => {
  void _ignoredFormat;
  void _ignoredAmpm;
  const [innerValue, setInnerValue] = useTrackedValue({ value, defaultValue });
  const handleChange = (newValue, ctx) => {
    if (sameTime(newValue, innerValue)) return;
    setInnerValue(newValue);
    if (isValidOrClear(newValue)) onChange?.(newValue, ctx);
  };
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DualFormatBox
        innerValue={innerValue}
        handleChange={handleChange}
        readOnly={readOnly}
        disabled={disabled}
        outerSx={sx}
      />
    </LocalizationProvider>
  );
};

export const MobileTimePickerWithIcon = ({
  value,
  defaultValue,
  onChange,
  format: _ignoredFormat,
  ampm: _ignoredAmpm,
  readOnly,
  disabled,
  sx,
  slotProps,
  ...rest
}) => {
  void _ignoredFormat;
  void _ignoredAmpm;
  const [innerValue, setInnerValue] = useTrackedValue({ value, defaultValue });
  const [dialOpen, setDialOpen] = useState(false);
  const handleChange = (newValue, ctx) => {
    if (sameTime(newValue, innerValue)) return;
    setInnerValue(newValue);
    // Same guard as CustomTimeInput: only notify the caller when valid/clear.
    if (isValidOrClear(newValue)) onChange?.(newValue, ctx);
  };
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DualFormatBox
        innerValue={innerValue}
        handleChange={handleChange}
        readOnly={readOnly}
        disabled={disabled}
        outerSx={sx}
        withClockButton
        onClockClick={(e) => {
          e.stopPropagation();
          if (!readOnly && !disabled) setDialOpen(true);
        }}
      />
      <MobileTimePicker
        {...rest}
        value={innerValue ?? null}
        onChange={handleChange}
        open={dialOpen}
        onClose={() => setDialOpen(false)}
        readOnly={readOnly}
        disabled={disabled}
        format="HH:mm"
        ampm={false}
        slotProps={{
          ...(slotProps || {}),
          textField: { sx: { display: "none" } },
        }}
      />
    </LocalizationProvider>
  );
};
